import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { MerchantPicker } from './MerchantPicker'
import { useCurrentMerchantCountryCode, useMyMerchants, usePermissions } from '../../hooks'
import { getMerchantSearchCountry } from '../../hooks/permissionsV2'
import { formatCountryOptionLabel } from '../../utils'
import { Modal } from '../rewards-ui'
import { Select } from '@ui'

const ModalStyled = styled(Modal)`
	max-width: 700px;
	min-width: 500px;
`

const SelectorWrapper = styled.div`
	margin: 24px 66px 12px;
`

const SelectorLabel = styled.div`
	margin-bottom: 8px;
	font-weight: 600;
`

const MerchantSelectorPopup = ({ open = false, onClose }) => {
	const { t: translate } = useTranslation()
	const { myMerchants, loading } = useMyMerchants()
	const { allowedMerchantCountries } = usePermissions()
	const { currentMerchantCountryCode } = useCurrentMerchantCountryCode()
	const [selectedCountry, setSelectedCountry] = useState('')

	const defaultCountry = getMerchantSearchCountry({
		allowedMerchantCountries,
		currentMerchantCountryCode,
		myMerchants,
	})

	useEffect(() => {
		setSelectedCountry(defaultCountry)
	}, [defaultCountry])

	const countryOptions = useMemo(
		() =>
			allowedMerchantCountries.map(countryCode => ({
				label: formatCountryOptionLabel(countryCode),
				value: countryCode,
			})),
		[allowedMerchantCountries],
	)
	
	if (!open) return null

	return (
		<ModalStyled onClose={onClose} show={open} open title={translate('merchantSelector.selectMerchant')}>
			{!loading && (
				<>
					{countryOptions.length > 1 && (
						<SelectorWrapper>
							<SelectorLabel>{translate('merchantSelector.countrySelector.label')}</SelectorLabel>
							<Select
								items={countryOptions}
								value={selectedCountry}
								onChange={countryCode => setSelectedCountry(countryCode)}
								aria-label={translate('merchantSelector.countrySelector.label')}
							/>
						</SelectorWrapper>
					)}
					<MerchantPicker
						myMerchants={myMerchants}
						onClose={onClose}
						selectedCountry={selectedCountry}
						compactTopSpacing={countryOptions.length > 1}
					/>
				</>
			)}
		</ModalStyled>
	)
}

MerchantSelectorPopup.propTypes = {
	open: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
}

export { MerchantSelectorPopup }
